import { useCallback, useEffect, useState } from 'react';
import {
  account as loadAccount,
  adjust,
  adjustmentReasons,
  applications,
  entries as loadEntries,
  type AccountView,
  type AdjustmentReason,
  type ApplicationRow,
  type LedgerRow,
} from './api';
import type { LoyaltyTranslate } from './i18n';
import {
  EmptyState,
  Field,
  Id,
  MoneyInput,
  Select,
  SkeletonRows,
  Table,
  TextInput,
} from '../../shell/kit';
import { CustomerPicker } from '../../shell/pickers';
import { formatMoney } from '../../shell/format';
import { ReferralCard } from './referrals';

const FIELD = 'rounded border border-line-strong px-2 py-1 text-sm';

export interface AccountTabProps {
  readonly t: LoyaltyTranslate;
  readonly locale: 'ru' | 'kk';
  readonly onError: (message: string) => void;
}

export function AccountTab({ t, locale, onError }: AccountTabProps) {
  const [customerId, setCustomerId] = useState('');
  const [view, setView] = useState<AccountView | undefined>(undefined);
  const [rows, setRows] = useState<readonly LedgerRow[]>([]);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [reasons, setReasons] = useState<readonly AdjustmentReason[]>([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [noAccount, setNoAccount] = useState(false);

  const [direction, setDirection] = useState<'credit' | 'debit'>('credit');
  const [amountMinor, setAmountMinor] = useState<number | null>(null);
  const [reasonCode, setReasonCode] = useState('');
  const [comment, setComment] = useState('');

  useEffect(() => {
    adjustmentReasons()
      .then((list) => {
        setReasons(list);
        setReasonCode(list[0]?.code ?? '');
      })
      .catch((err: Error) => onError(err.message));
  }, [onError]);

  const open = useCallback(
    async (id: string, since?: string): Promise<void> => {
      if (!id.trim()) return;
      setBusy(true);
      setNoAccount(false);
      try {
        const next = await loadEntries(id.trim(), since);
        setView(await loadAccount(id.trim()));
        setRows((current) => (since ? [...current, ...next.items] : next.items));
        setCursor(next.nextCursor);
      } catch (err) {
        /*
         * «Счёта нет» — состояние клиента, а не сбой: счёт заводится первым
         * начислением, и у трети базы его пока нет. Пока идентификатор
         * вставляли руками, красную ошибку видел тот, кто заведомо знал, куда
         * идёт; с поиском по телефону в неё упирается любой, кто открыл
         * нового клиента.
         */
        if ((err as { code?: string }).code === 'E04.account_not_found') {
          setNoAccount(true);
          setView(undefined);
          setRows([]);
          setCursor(undefined);
          return;
        }
        onError((err as Error).message);
      } finally {
        setBusy(false);
      }
    },
    [onError],
  );

  const visible = reasons.filter(
    (r) => r.allowedDirection === 'both' || r.allowedDirection === direction,
  );

  function submitAdjust(): void {
    setBusy(true);
    setDone(false);
    adjust({
      customerId: customerId.trim(),
      direction,
      amountMinor: amountMinor ?? 0,
      reasonCode,
      ...(comment.trim() ? { comment: comment.trim() } : {}),
    })
      .then(async () => {
        setDone(true);
        setAmountMinor(null);
        setComment('');
        await open(customerId);
      })
      .catch((err: Error) => onError(err.message))
      .finally(() => setBusy(false));
  }

  return (
    <section className="space-y-4">
      <header className="flex flex-wrap items-end gap-2">
        {/*
          * Поиск, а не ввод ULID. Здесь до последнего стояло поле
          * «Идентификатор клиента» под двадцать шесть символов: пикер завели
          * именно ради этого случая, но подключили тогда только к формам
          * идентификации, а карточку лояльности пропустили. Откуда маркетолог
          * возьмёт идентификатор, по-прежнему не объяснялось нигде.
          */}
        <Field label={t('account.search')} hint={t('account.searchHint')}>
          <div className="w-80">
            <CustomerPicker
              value={customerId}
              disabled={busy}
              onChange={(id) => {
                setCustomerId(id);
                // Смена клиента не должна оставлять на экране чужой баланс
                // и чужие строки реестра.
                setView(undefined);
                setRows([]);
                setCursor(undefined);
                setDone(false);
                setNoAccount(false);
                if (id) void open(id);
              }}
            />
          </div>
        </Field>
        <button
          type="button"
          disabled={busy || !customerId}
          className="inline-flex h-8 items-center justify-center rounded-control bg-brand-600 px-3 text-sm font-medium text-ink-invert shadow-card transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-sunken disabled:text-ink-muted disabled:shadow-none"
          onClick={() => void open(customerId)}
        >
          {t('account.reload')}
        </button>
      </header>

      {customerId ? <ReferralCard t={t} customerId={customerId} onError={onError} /> : null}

      {noAccount ? (
        <EmptyState title={t('account.none')} reason={t('account.noneHint')} />
      ) : null}

      {view && (
        <>
          <dl className="grid grid-cols-1 gap-3 rounded border border-line bg-surface p-4 sm:grid-cols-3">
            <div>
              <dt className="text-xs text-ink-muted">{t('account.ledgerBalance')}</dt>
              <dd className="text-lg font-semibold tabular">{formatMoney(view.balanceMinor)}</dd>
            </div>
            <div>
              <dt className="text-xs text-ink-muted">{t('account.materialized')}</dt>
              <dd className="text-lg tabular">{formatMoney(view.materializedMinor)}</dd>
            </div>
            <div>
              <dt className="text-xs text-ink-muted">{t('account.drift')}</dt>
              <dd
                className={`text-lg tabular ${view.driftMinor === 0 ? 'text-ink-muted' : 'text-danger'}`}
              >
                {formatMoney(view.driftMinor)}
              </dd>
            </div>
          </dl>

          <div className="rounded border border-line bg-surface p-4">
            <h4 className="mb-3 font-medium text-ink">{t('adjust.title')}</h4>
            <div className="flex flex-wrap items-end gap-3">
              <Field label={t('account.direction')}>
                <Select
                  value={direction}
                  onChange={(e) => setDirection(e.target.value as 'credit' | 'debit')}
                >
                  <option value="credit">{t('account.credit')}</option>
                  <option value="debit">{t('account.debit')}</option>
                </Select>
              </Field>
              <label className="text-xs text-ink-muted">
                {/* Сумма вводится в тенге. Раньше поле не имело единиц, а рядом
                    печатались тиыны без форматирования: управляющий, называющий
                    клиенту баланс, ошибался в сто раз. */}
                <span className="mb-1 block">{t('account.amount')}</span>
                <MoneyInput
                  className="w-40"
                  valueMinor={amountMinor}
                  onChangeMinor={setAmountMinor}
                />
              </label>
              <Field label={t('adjust.reason')}>
                <Select value={reasonCode} onChange={(e) => setReasonCode(e.target.value)}>
                  {visible.map((r) => (
                    <option key={r.code} value={r.code}>
                      {locale === 'kk' ? r.labelKk : r.labelRu}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label={t('adjust.comment')}>
                <TextInput
                  className={`${FIELD} w-64`}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                />
              </Field>
              <button
                type="button"
                disabled={busy || !reasonCode || !amountMinor}
                className="inline-flex h-8 items-center justify-center rounded-control bg-brand-600 px-3 text-sm font-medium text-ink-invert shadow-card transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-sunken disabled:text-ink-muted disabled:shadow-none"
                onClick={submitAdjust}
              >
                {t('adjust.submit')}
              </button>
              {done && <span className="text-xs text-ok">{t('adjust.done')}</span>}
            </div>
          </div>

          <div className="overflow-x-auto rounded border border-line bg-surface">
            <Table className="text-left">
              <thead className="bg-sunken text-xs uppercase text-ink-muted">
                <tr>
                  <th className="px-3 py-2">{t('account.date')}</th>
                  <th className="px-3 py-2">{t('account.direction')}</th>
                  <th className="px-3 py-2">{t('account.amount')}</th>
                  <th className="px-3 py-2">{t('account.reason')}</th>
                  <th className="px-3 py-2">{t('account.rule')}</th>
                </tr>
              </thead>
              <tbody>
                {busy && rows.length === 0 ? <SkeletonRows rows={4} columns={5} /> : null}
                {rows.map((row) => (
                  <tr key={row.id} className="border-t border-line">
                    <td className="px-3 py-2 text-xs">
                      {new Date(row.createdAt).toLocaleString()}
                    </td>
                    <td className="px-3 py-2">
                      {row.direction === 'credit' ? t('account.credit') : t('account.debit')}
                    </td>
                    <td
                      className={`px-3 py-2 font-medium ${
                        row.direction === 'credit' ? 'text-ok' : 'text-ink'
                      }`}
                    >
                      {row.direction === 'credit' ? '+' : '−'}
                      {formatMoney(row.amountMinor)}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {row.reasonCode}
                      {row.comment ? ` · ${row.comment}` : ''}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">
                      {row.ruleCode ? `${row.ruleCode} v${row.ruleVersion}` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {cursor && (
            <button
              type="button"
              className="inline-flex h-8 items-center justify-center rounded-control border border-line-strong px-3 text-sm font-medium text-ink transition hover:bg-sunken disabled:cursor-not-allowed disabled:text-ink-faint"
              onClick={() => void open(customerId, cursor)}
            >
              {t('account.more')}
            </button>
          )}

          <Applications t={t} customerId={customerId} onError={onError} />
        </>
      )}
    </section>
  );
}

/**
 * Решения движка по клиенту.
 *
 * `loyalty_application_log` заполняется на каждой оценке заказа и отвечает на
 * вопрос, который в поддержке звучит чаще всех: «почему не начислили». Маршрут
 * был, экрана не было — причину отказа доставали запросом в базу.
 */
function Applications({
  t,
  customerId,
  onError,
}: {
  t: LoyaltyTranslate;
  customerId: string;
  onError: (message: string) => void;
}) {
  const [rows, setRows] = useState<readonly ApplicationRow[] | null>(null);

  useEffect(() => {
    let alive = true;
    setRows(null);
    applications(customerId)
      .then((list) => {
        if (alive) setRows(list);
      })
      .catch((err: Error) => {
        if (alive) {
          setRows([]);
          onError(err.message);
        }
      });
    return () => {
      alive = false;
    };
  }, [customerId, onError]);

  return (
    <section className="mt-6">
      <h3 className="text-sm font-semibold text-ink">{t('apps.title')}</h3>
      <p className="mt-1 mb-2 text-xs text-ink-muted">{t('apps.hint')}</p>

      {rows === null ? (
        <div className="overflow-x-auto rounded border border-line bg-surface">
          <Table className="text-left">
            <tbody>
              <SkeletonRows rows={3} columns={5} />
            </tbody>
          </Table>
        </div>
      ) : rows.length === 0 ? (
        <EmptyState title={t('apps.empty')} reason={t('apps.emptyHint')} />
      ) : (
        <div className="overflow-x-auto rounded border border-line bg-surface">
          <Table className="text-left">
            <thead className="bg-sunken text-xs uppercase text-ink-muted">
              <tr>
                <th className="px-3 py-2">{t('apps.when')}</th>
                <th className="px-3 py-2">{t('apps.outcome')}</th>
                <th className="px-3 py-2">{t('apps.reason')}</th>
                <th className="px-3 py-2">{t('apps.amount')}</th>
                <th className="px-3 py-2">{t('apps.order')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-t border-line">
                  <td className="px-3 py-2 text-xs">
                    {new Date(row.evaluated_at).toLocaleString()}
                  </td>
                  <td className="px-3 py-2">
                    <span className={row.outcome === 'accrued' ? 'text-ok' : 'text-ink-muted'}>
                      {row.outcome}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs">{row.reason_code ?? '—'}</td>
                  <td className="px-3 py-2">
                    {row.amount_minor ? formatMoney(Number(row.amount_minor)) : '—'}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">
                    {row.order_id ? <Id value={row.order_id} /> : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </section>
  );
}
